// =============================================================================
// DEV-191: Tenant Configuration Management
// =============================================================================

/**
 * Database configuration for a tenant (DEV-191)
 */
export interface TenantDatabaseConfig {
  /** Database connection pool size */
  poolSize: number;
  /** Maximum connections allowed */
  maxConnections: number;
  /** Connection timeout in seconds */
  connectionTimeout: number;
  /** Idle timeout in seconds */
  idleTimeout: number;
  /** Enable query logging */
  enableQueryLogging: boolean;
  /** Enable slow query logging */
  enableSlowQueryLog: boolean;
  /** Slow query threshold in milliseconds */
  slowQueryThreshold: number;
  /** Read replica enabled */
  useReadReplica: boolean;
  /** SSL mode: disable, require, verify-ca, verify-full */
  sslMode: 'disable' | 'require' | 'verify-ca' | 'verify-full';
}

/**
 * Storage configuration for a tenant (DEV-191)
 */
export interface TenantStorageConfig {
  /** Maximum storage quota in GB */
  storageQuotaGb: number;
  /** Current storage used in GB */
  storageUsedGb: number;
  /** Maximum file size in MB */
  maxFileSizeMb: number;
  /** Allowed file types (MIME types) */
  allowedFileTypes: string[];
  /** Enable file versioning */
  enableVersioning: boolean;
  /** Retention period in days */
  retentionDays: number;
  /** Storage tier: standard, premium, archive */
  storageTier: 'standard' | 'premium' | 'archive';
  /** Enable encryption at rest */
  encryptionAtRest: boolean;
  /** Backup frequency: hourly, daily, weekly */
  backupFrequency: 'hourly' | 'daily' | 'weekly';
}

/**
 * Networking configuration for a tenant (DEV-191)
 */
export interface TenantNetworkingConfig {
  /** Rate limit requests per minute */
  rateLimitPerMinute: number;
  /** Rate limit requests per hour */
  rateLimitPerHour: number;
  /** Maximum concurrent connections */
  maxConcurrentConnections: number;
  /** IP whitelist (empty = allow all) */
  ipWhitelist: string[];
  /** IP blacklist */
  ipBlacklist: string[];
  /** Enable CORS */
  enableCors: boolean;
  /** Allowed CORS origins */
  corsOrigins: string[];
  /** Enable WebSocket connections */
  enableWebSocket: boolean;
  /** WebSocket message size limit in KB */
  wsMessageSizeKb: number;
  /** API timeout in seconds */
  apiTimeoutSeconds: number;
  /** Enable request compression */
  enableCompression: boolean;
}

/**
 * Security configuration for a tenant (DEV-191)
 */
export interface TenantSecurityConfig {
  /** Enable MFA requirement */
  requireMfa: boolean;
  /** MFA methods allowed: totp, sms, email */
  mfaMethods: ('totp' | 'sms' | 'email')[];
  /** Session timeout in minutes */
  sessionTimeoutMinutes: number;
  /** Maximum concurrent sessions per user */
  maxConcurrentSessions: number;
  /** Password minimum length */
  passwordMinLength: number;
  /** Require uppercase in password */
  passwordRequireUppercase: boolean;
  /** Require lowercase in password */
  passwordRequireLowercase: boolean;
  /** Require numbers in password */
  passwordRequireNumbers: boolean;
  /** Require special characters in password */
  passwordRequireSpecial: boolean;
  /** Password expiry days (0 = never) */
  passwordExpiryDays: number;
  /** Password history count */
  passwordHistoryCount: number;
  /** Account lockout threshold (failed attempts) */
  lockoutThreshold: number;
  /** Account lockout duration in minutes */
  lockoutDurationMinutes: number;
  /** Enable audit logging */
  enableAuditLogging: boolean;
  /** Audit log retention days */
  auditLogRetentionDays: number;
  /** Enable data encryption */
  enableDataEncryption: boolean;
  /** Encryption algorithm */
  encryptionAlgorithm: 'AES-256-GCM' | 'AES-256-CBC' | 'ChaCha20-Poly1305';
}

/**
 * Feature flags configuration for a tenant (DEV-191)
 */
export interface TenantFeatureFlagsConfig {
  /** Core features */
  trading: boolean;
  analytics: boolean;
  reporting: boolean;
  backtesting: boolean;
  /** Advanced features */
  mlModels: boolean;
  alerting: boolean;
  automation: boolean;
  apiAccess: boolean;
  /** Premium features */
  dedicatedSupport: boolean;
  customIntegrations: boolean;
  advancedSecurity: boolean;
  multiRegion: boolean;
  /** Experimental features */
  betaFeatures: boolean;
  experimentalApis: boolean;
  /** Custom feature flags */
  customFlags: Record<string, boolean>;
}

/**
 * Branding configuration for tenant customization (DEV-1050)
 * Extended configuration for comprehensive tenant branding management
 */
export interface TenantBrandingConfig {
  /** Enable custom branding for this tenant */
  enabled: boolean;

  // === Logo & Visual Identity ===
  /** Primary logo URL (header/main display) */
  logoUrl: string;
  /** Logo alt text for accessibility */
  logoAltText: string;
  /** Logo dimensions */
  logoWidth: number;
  logoHeight: number;
  /** Favicon URL (16x16 or 32x32 recommended) */
  faviconUrl: string;
  /** Login page background image */
  loginBackgroundUrl: string;

  // === Color Palette ===
  /** Primary brand color (hex) */
  primaryColor: string;
  /** Secondary brand color (hex) */
  secondaryColor: string;
  /** Accent color for highlights (hex) */
  accentColor: string;
  /** Success state color (hex) */
  successColor: string;
  /** Warning state color (hex) */
  warningColor: string;
  /** Error/danger state color (hex) */
  errorColor: string;
  /** Text color on primary background (hex) */
  primaryTextColor: string;
  /** Text color on secondary background (hex) */
  secondaryTextColor: string;

  // === Typography ===
  /** Primary font family */
  fontFamily: string;
  /** Heading font family (optional, falls back to fontFamily) */
  headingFontFamily: string;
  /** Base font size in pixels */
  baseFontSize: number;
  /** Custom Google Font URL (optional) */
  customFontUrl: string;

  // === UI Customization ===
  /** Border radius for components (px) */
  borderRadius: number;
  /** Button style variant */
  buttonStyle: 'rounded' | 'square' | 'pill';
  /** Card shadow intensity */
  shadowIntensity: 'none' | 'light' | 'medium' | 'heavy';
  /** Dark mode preference */
  darkModePreference: 'light' | 'dark' | 'system';

  // === Content & Messaging ===
  /** Welcome message on login page */
  welcomeMessage: string;
  /** Footer text */
  footerText: string;
  /** Support contact email */
  supportEmail: string;
  /** Support contact phone */
  supportPhone: string;
  /** Custom terms of service URL */
  termsUrl: string;
  /** Custom privacy policy URL */
  privacyUrl: string;

  // === Email Templates ===
  /** Email header logo URL */
  emailLogoUrl: string;
  /** Email header background color */
  emailHeaderColor: string;
  /** Email footer text */
  emailFooterText: string;

  // === Advanced ===
  /** Custom CSS (sanitized before application) */
  customCss: string;
  /** Custom JavaScript for analytics/tracking (sanitized) */
  customScripts: string;
  /** Allowed script domains for CSP */
  allowedScriptDomains: string[];
}

/**
 * Limits and quotas for a tenant (DEV-191)
 */
export interface TenantLimitsConfig {
  /** Maximum users allowed */
  maxUsers: number;
  /** Maximum API keys per user */
  maxApiKeysPerUser: number;
  /** Maximum strategies */
  maxStrategies: number;
  /** Maximum active orders */
  maxActiveOrders: number;
  /** Maximum daily trades */
  maxDailyTrades: number;
  /** Maximum portfolio value (0 = unlimited) */
  maxPortfolioValue: number;
  /** Maximum daily API calls */
  maxDailyApiCalls: number;
  /** Maximum WebSocket subscriptions */
  maxWsSubscriptions: number;
  /** Maximum report size in MB */
  maxReportSizeMb: number;
  /** Maximum data export size in MB */
  maxExportSizeMb: number;
}

/**
 * ML (Machine Learning) configuration for a tenant
 */
export interface TenantMlConfig {
  /** Maximum ML models per tenant */
  maxModels: number;
  /** ML training timeout in minutes */
  trainingTimeoutMinutes: number;
  /** ML inference timeout in seconds */
  inferenceTimeoutSeconds: number;
  /** Max ML dataset size in MB */
  maxDatasetSizeMb: number;
  /** Enable automatic ML model retraining */
  autoRetrainEnabled: boolean;
  /** Enable ML model versioning */
  modelVersioningEnabled: boolean;
  /** Max concurrent ML training jobs */
  maxTrainingJobs: number;
  /** Allowed ML model types */
  allowedModelTypes: string[];
  /** GPU quota hours per month (0 = no GPU) */
  gpuQuotaHours: number;
}

/**
 * Comprehensive tenant configuration (DEV-191, DEV-1050)
 */
export interface TenantConfiguration {
  /** Tenant ID */
  tenantId: string;
  /** Database configuration */
  database: TenantDatabaseConfig;
  /** Storage configuration */
  storage: TenantStorageConfig;
  /** Networking configuration */
  networking: TenantNetworkingConfig;
  /** Security configuration */
  security: TenantSecurityConfig;
  /** Feature flags */
  featureFlags: TenantFeatureFlagsConfig;
  /** Limits and quotas */
  limits: TenantLimitsConfig;
  /** Branding configuration (DEV-1050) */
  branding: TenantBrandingConfig;
  /** ML configuration */
  mlConfig: TenantMlConfig;
  /** Configuration version */
  version: number;
  /** Last updated timestamp */
  updatedAt: Date;
  /** Updated by user ID */
  updatedBy: string;
}

/**
 * Default configuration values (DEV-191)
 */
export const DEFAULT_TENANT_DATABASE_CONFIG: TenantDatabaseConfig = {
  poolSize: 10,
  maxConnections: 50,
  connectionTimeout: 30,
  idleTimeout: 600,
  enableQueryLogging: false,
  enableSlowQueryLog: true,
  slowQueryThreshold: 1000,
  useReadReplica: false,
  sslMode: 'require'
};

export const DEFAULT_TENANT_STORAGE_CONFIG: TenantStorageConfig = {
  storageQuotaGb: 50,
  storageUsedGb: 0,
  maxFileSizeMb: 100,
  allowedFileTypes: ['application/pdf', 'image/png', 'image/jpeg', 'text/csv', 'application/json'],
  enableVersioning: true,
  retentionDays: 90,
  storageTier: 'standard',
  encryptionAtRest: true,
  backupFrequency: 'daily'
};

export const DEFAULT_TENANT_NETWORKING_CONFIG: TenantNetworkingConfig = {
  rateLimitPerMinute: 100,
  rateLimitPerHour: 1000,
  maxConcurrentConnections: 100,
  ipWhitelist: [],
  ipBlacklist: [],
  enableCors: true,
  corsOrigins: [],
  enableWebSocket: true,
  wsMessageSizeKb: 64,
  apiTimeoutSeconds: 30,
  enableCompression: true
};

export const DEFAULT_TENANT_SECURITY_CONFIG: TenantSecurityConfig = {
  requireMfa: false,
  mfaMethods: ['totp'],
  sessionTimeoutMinutes: 60,
  maxConcurrentSessions: 5,
  passwordMinLength: 12,
  passwordRequireUppercase: true,
  passwordRequireLowercase: true,
  passwordRequireNumbers: true,
  passwordRequireSpecial: true,
  passwordExpiryDays: 90,
  passwordHistoryCount: 5,
  lockoutThreshold: 5,
  lockoutDurationMinutes: 30,
  enableAuditLogging: true,
  auditLogRetentionDays: 365,
  enableDataEncryption: true,
  encryptionAlgorithm: 'AES-256-GCM'
};

export const DEFAULT_TENANT_FEATURE_FLAGS_CONFIG: TenantFeatureFlagsConfig = {
  trading: true,
  analytics: true,
  reporting: true,
  backtesting: false,
  mlModels: false,
  alerting: true,
  automation: false,
  apiAccess: true,
  dedicatedSupport: false,
  customIntegrations: false,
  advancedSecurity: false,
  multiRegion: false,
  betaFeatures: false,
  experimentalApis: false,
  customFlags: {}
};

export const DEFAULT_TENANT_LIMITS_CONFIG: TenantLimitsConfig = {
  maxUsers: 10,
  maxApiKeysPerUser: 5,
  maxStrategies: 10,
  maxActiveOrders: 100,
  maxDailyTrades: 1000,
  maxPortfolioValue: 0, // 0 = unlimited
  maxDailyApiCalls: 10000,
  maxWsSubscriptions: 50,
  maxReportSizeMb: 50,
  maxExportSizeMb: 100
};

/**
 * Default branding configuration (DEV-1050)
 */
export const DEFAULT_TENANT_BRANDING_CONFIG: TenantBrandingConfig = {
  enabled: false,
  // Logo & Visual Identity
  logoUrl: '',
  logoAltText: 'Company Logo',
  logoWidth: 150,
  logoHeight: 40,
  faviconUrl: '',
  loginBackgroundUrl: '',
  // Color Palette (Razor Edge defaults)
  primaryColor: '#3B82F6',      // Blue-500
  secondaryColor: '#6B7280',    // Gray-500
  accentColor: '#10B981',       // Emerald-500
  successColor: '#22C55E',      // Green-500
  warningColor: '#F59E0B',      // Amber-500
  errorColor: '#EF4444',        // Red-500
  primaryTextColor: '#FFFFFF',  // White
  secondaryTextColor: '#F3F4F6', // Gray-100
  // Typography
  fontFamily: 'Inter, system-ui, sans-serif',
  headingFontFamily: '',        // Falls back to fontFamily
  baseFontSize: 16,
  customFontUrl: '',
  // UI Customization
  borderRadius: 8,
  buttonStyle: 'rounded',
  shadowIntensity: 'medium',
  darkModePreference: 'system',
  // Content & Messaging
  welcomeMessage: 'Welcome to RazorEdge Trading Platform',
  footerText: '© {year} Razor Fintech. All rights reserved.',
  supportEmail: 'support@razorfintech.io',
  supportPhone: '',
  termsUrl: '',
  privacyUrl: '',
  // Email Templates
  emailLogoUrl: '',
  emailHeaderColor: '#3B82F6',
  emailFooterText: '',
  // Advanced
  customCss: '',
  customScripts: '',
  allowedScriptDomains: []
};

export const DEFAULT_TENANT_ML_CONFIG: TenantMlConfig = {
  maxModels: 10,
  trainingTimeoutMinutes: 120,
  inferenceTimeoutSeconds: 30,
  maxDatasetSizeMb: 500,
  autoRetrainEnabled: false,
  modelVersioningEnabled: true,
  maxTrainingJobs: 3,
  allowedModelTypes: ['regression', 'classification', 'timeseries'],
  gpuQuotaHours: 0
};

/**
 * Deep-freeze every DEFAULT_TENANT_*_CONFIG so a caller that forgets to clone
 * and mutates `DEFAULT_TENANT_STORAGE_CONFIG.allowedFileTypes.push(...)` gets a
 * runtime TypeError in strict mode instead of silently corrupting the defaults
 * shared by every subsequent tenant. `createDefaultTenantConfiguration` already
 * uses structuredClone; this freeze is a second layer of defence for direct
 * imports.
 */
function deepFreeze<T>(obj: T): T {
  if (obj && typeof obj === 'object' && !Object.isFrozen(obj)) {
    Object.freeze(obj);
    for (const key of Object.keys(obj) as Array<keyof T>) {
      const value = obj[key];
      if (value && typeof value === 'object') {
        deepFreeze(value);
      }
    }
  }
  return obj;
}
deepFreeze(DEFAULT_TENANT_DATABASE_CONFIG);
deepFreeze(DEFAULT_TENANT_STORAGE_CONFIG);
deepFreeze(DEFAULT_TENANT_NETWORKING_CONFIG);
deepFreeze(DEFAULT_TENANT_SECURITY_CONFIG);
deepFreeze(DEFAULT_TENANT_FEATURE_FLAGS_CONFIG);
deepFreeze(DEFAULT_TENANT_LIMITS_CONFIG);
deepFreeze(DEFAULT_TENANT_BRANDING_CONFIG);
deepFreeze(DEFAULT_TENANT_ML_CONFIG);

/**
 * Create default tenant configuration (DEV-191, DEV-1050).
 *
 * Uses structuredClone so nested arrays/objects (ipWhitelist, allowedFileTypes,
 * mfaMethods, customFlags, allowedScriptDomains, allowedModelTypes) are NOT
 * shared with the DEFAULT_TENANT_*_CONFIG constants. A caller that mutates
 * `cfg.networking.ipWhitelist.push(...)` must not leak into the next tenant's
 * defaults — this is a multi-tenant isolation invariant.
 *
 * Note on `tenantId` validation: unlike `createDefaultSubscription`, which
 * throws on empty input, this factory intentionally accepts an empty string.
 * `tenantConfigurationService.compareWithDefaults` calls it with `''` to
 * generate the canonical "reset" config for diffing against the current
 * tenant's config — the result is used read-only and never persisted.
 */
export function createDefaultTenantConfiguration(tenantId: string, updatedBy: string): TenantConfiguration {
  return {
    tenantId,
    database: structuredClone(DEFAULT_TENANT_DATABASE_CONFIG),
    storage: structuredClone(DEFAULT_TENANT_STORAGE_CONFIG),
    networking: structuredClone(DEFAULT_TENANT_NETWORKING_CONFIG),
    security: structuredClone(DEFAULT_TENANT_SECURITY_CONFIG),
    featureFlags: structuredClone(DEFAULT_TENANT_FEATURE_FLAGS_CONFIG),
    limits: structuredClone(DEFAULT_TENANT_LIMITS_CONFIG),
    branding: structuredClone(DEFAULT_TENANT_BRANDING_CONFIG),
    mlConfig: structuredClone(DEFAULT_TENANT_ML_CONFIG),
    version: 1,
    updatedAt: new Date(),
    updatedBy
  };
}

/**
 * Configuration section types for UI tabs (DEV-191, DEV-1050)
 */
export type ConfigurationSection = 'database' | 'storage' | 'networking' | 'security' | 'featureFlags' | 'limits' | 'branding' | 'mlConfig';

/**
 * Configuration section metadata for UI (DEV-191)
 */
export interface ConfigSectionInfo {
  section: ConfigurationSection;
  label: string;
  description: string;
  icon: string;
}

export const CONFIG_SECTION_INFO: Record<ConfigurationSection, ConfigSectionInfo> = {
  database: {
    section: 'database',
    label: 'Database',
    description: 'Database connection and performance settings',
    icon: 'pi pi-database'
  },
  storage: {
    section: 'storage',
    label: 'Storage',
    description: 'File storage and backup configuration',
    icon: 'pi pi-folder'
  },
  networking: {
    section: 'networking',
    label: 'Networking',
    description: 'Rate limits, CORS, and connection settings',
    icon: 'pi pi-globe'
  },
  security: {
    section: 'security',
    label: 'Security',
    description: 'Authentication, authorization, and encryption',
    icon: 'pi pi-shield'
  },
  featureFlags: {
    section: 'featureFlags',
    label: 'Features',
    description: 'Enable or disable tenant features',
    icon: 'pi pi-flag'
  },
  limits: {
    section: 'limits',
    label: 'Limits',
    description: 'Usage quotas and resource limits',
    icon: 'pi pi-chart-bar'
  },
  branding: {
    section: 'branding',
    label: 'Branding',
    description: 'Visual identity, colors, and customization',
    icon: 'pi pi-palette'
  },
  mlConfig: {
    section: 'mlConfig',
    label: 'ML Config',
    description: 'Machine learning models, training, and inference settings',
    icon: 'pi pi-microchip'
  }
};
